import { previewSenderProxies, uploadSenderProxies } from '@/api/admin';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select';
import type {
	SenderProxyFormat,
	SenderProxyPreviewResult,
	SenderProxyProtocol,
	SenderProxyUploadResult,
} from '@/entities/sender.entity';
import { formatApiError } from '@/lib/format-api-error';
import axios from 'axios';
import { CheckCircle2Icon, FileUpIcon } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { proxyFormatLabel } from './proxy-labels';

type ImportUiState =
	| { phase: 'idle' }
	| { phase: 'importing' }
	| { phase: 'success'; result: SenderProxyUploadResult; filename: string }
	| { phase: 'error'; message: string };

interface ProxyUploadWizardProps {
	protocol: SenderProxyProtocol;
	format: SenderProxyFormat;
	onProtocolChange: (protocol: SenderProxyProtocol) => void;
	onFormatChange: (format: SenderProxyFormat) => void;
	onImported: () => void;
}

function formatImportError(error: unknown): string {
	if (axios.isAxiosError(error)) {
		return formatApiError(error.response?.data) ?? error.message;
	}
	if (error instanceof Error) {
		return error.message;
	}
	return 'Не удалось импортировать прокси';
}

export default function ProxyUploadWizard({
	protocol,
	format,
	onProtocolChange,
	onFormatChange,
	onImported,
}: ProxyUploadWizardProps) {
	const fileInputRef = useRef<HTMLInputElement>(null);
	const previewSeqRef = useRef(0);
	const [file, setFile] = useState<File | null>(null);
	const [preview, setPreview] = useState<SenderProxyPreviewResult | null>(null);
	const [previewError, setPreviewError] = useState<string | null>(null);
	const [isPreviewing, setIsPreviewing] = useState(false);
	const [importUi, setImportUi] = useState<ImportUiState>({ phase: 'idle' });

	const isImporting = importUi.phase === 'importing';
	const isSuccess = importUi.phase === 'success';
	const importError = importUi.phase === 'error' ? importUi.message : null;

	async function runPreview(selected: File, proto: SenderProxyProtocol, fmt: SenderProxyFormat) {
		const seq = ++previewSeqRef.current;
		setPreview(null);
		setPreviewError(null);
		setIsPreviewing(true);
		try {
			const result = await previewSenderProxies(selected, { protocol: proto, format: fmt });
			if (seq !== previewSeqRef.current) {
				return;
			}
			setPreview(result);
			if (result.validCount === 0) {
				toast.error('Не найдено валидных прокси в файле');
			}
		} catch (error) {
			if (seq !== previewSeqRef.current) {
				return;
			}
			setPreview(null);
			setPreviewError(formatImportError(error));
		} finally {
			if (seq === previewSeqRef.current) {
				setIsPreviewing(false);
			}
		}
	}

	useEffect(() => {
		if (!file || isSuccess) {
			return;
		}
		void runPreview(file, protocol, format);
	}, [protocol, format, file, isSuccess]);

	function openFilePicker() {
		if (fileInputRef.current) {
			fileInputRef.current.value = '';
			fileInputRef.current.click();
		}
	}

	function resetForNewUpload() {
		setImportUi({ phase: 'idle' });
		setFile(null);
		setPreview(null);
		setPreviewError(null);
		previewSeqRef.current += 1;
		if (fileInputRef.current) {
			fileInputRef.current.value = '';
		}
	}

	async function handleImport() {
		if (!file || isSuccess) {
			return;
		}
		setImportUi({ phase: 'importing' });
		try {
			const result = await uploadSenderProxies(file, { protocol, format });
			setImportUi({ phase: 'success', result, filename: file.name });
			toast.success(
				`Импорт: +${result.added ?? 0} новых, обновлено ${result.updated ?? 0}. Всего в пуле: ${result.proxyCount}`,
			);
			onImported();
		} catch (error) {
			setImportUi({ phase: 'error', message: formatImportError(error) });
		}
	}

	const controlsDisabled = isPreviewing || isImporting || isSuccess;

	const canImport = Boolean(
		importUi.phase !== 'importing' &&
			importUi.phase !== 'success' &&
			preview &&
			preview.validCount > 0 &&
			!isPreviewing &&
			file,
	);

	function importHint(): string | null {
		if (isSuccess) {
			return null;
		}
		if (isImporting) {
			return 'Импорт файла на сервер…';
		}
		if (importError) {
			return 'Исправьте ошибку или попробуйте снова';
		}
		if (!file) {
			return 'Шаг 1: выберите txt-файл с прокси';
		}
		if (isPreviewing) {
			return 'Шаг 2: анализ файла…';
		}
		if (previewError) {
			return 'Ошибка анализа — проверьте файл и формат строк';
		}
		if (!preview) {
			return 'Шаг 2: дождитесь завершения анализа';
		}
		if (preview.validCount === 0) {
			return 'Нет валидных строк — смените формат или проверьте содержимое файла';
		}
		return 'Шаг 3: нажмите «Импортировать»';
	}

	const hint = importHint();

	return (
		<div className='flex flex-col gap-3 rounded-lg border border-border bg-muted/20 p-4'>
			<p className='text-sm font-medium'>Загрузка txt (merge)</p>
			<p className='text-xs text-muted-foreground'>Новый файл добавляет и обновляет прокси, ничего не удаляет.</p>

			{isSuccess ? (
				<div className='flex flex-col gap-3 rounded-md border border-emerald-500/40 bg-emerald-500/10 p-4'>
					<div className='flex items-start gap-2'>
						<CheckCircle2Icon className='text-emerald-600 dark:text-emerald-400 shrink-0 mt-0.5' size={20} />
						<div className='flex flex-col gap-1 text-sm'>
							<p className='font-medium text-emerald-700 dark:text-emerald-300'>Импорт выполнен</p>
							<p className='text-xs text-muted-foreground break-all'>Файл: {importUi.filename}</p>
							<p>
								+{importUi.result.added ?? 0} новых · обновлено {importUi.result.updated ?? 0} · всего в пуле{' '}
								{importUi.result.proxyCount}
							</p>
						</div>
					</div>
					<Button type='button' variant='outline' className='self-start' onClick={resetForNewUpload}>
						Загрузить ещё
					</Button>
				</div>
			) : null}

			<div className='grid gap-3 sm:grid-cols-2'>
				<div className='flex flex-col gap-1.5'>
					<Label>Протокол</Label>
					<Select
						value={protocol}
						disabled={controlsDisabled}
						onValueChange={v => onProtocolChange(v as SenderProxyProtocol)}
					>
						<SelectTrigger className='w-full'>
							<SelectValue />
						</SelectTrigger>
						<SelectContent>
							<SelectItem value='socks5'>SOCKS5</SelectItem>
							<SelectItem value='http'>HTTP</SelectItem>
						</SelectContent>
					</Select>
				</div>
				<div className='flex flex-col gap-1.5'>
					<Label>Формат строк</Label>
					<Select
						value={format}
						disabled={controlsDisabled}
						onValueChange={v => onFormatChange(v as SenderProxyFormat)}
					>
						<SelectTrigger className='w-full'>
							<SelectValue />
						</SelectTrigger>
						<SelectContent>
							<SelectItem value='auto'>Авто</SelectItem>
							<SelectItem value='host_port_user_pass'>host:port:user:pass</SelectItem>
							<SelectItem value='host_port'>host:port</SelectItem>
							<SelectItem value='url'>URL</SelectItem>
						</SelectContent>
					</Select>
				</div>
			</div>

			<input
				ref={fileInputRef}
				type='file'
				accept='.txt,text/plain'
				className='hidden'
				onChange={e => {
					const selected = e.target.files?.[0];
					if (!selected) {
						return;
					}
					setImportUi({ phase: 'idle' });
					setFile(selected);
					setPreview(null);
					setPreviewError(null);
				}}
			/>

			{!isSuccess ? (
				<button
					type='button'
					disabled={controlsDisabled}
					onClick={openFilePicker}
					className='flex flex-col items-center gap-2 rounded-lg border border-dashed border-border bg-card/50 px-4 py-6 text-center transition-colors hover:bg-muted/40 disabled:opacity-50'
				>
					<FileUpIcon className='text-muted-foreground' size={24} />
					<span className='text-sm font-medium'>
						{isPreviewing ? 'Анализ файла…' : file ? 'Выбрать другой файл' : 'Выбрать txt-файл'}
					</span>
					{file ? <span className='text-xs text-muted-foreground break-all'>{file.name}</span> : null}
				</button>
			) : null}

			{!isSuccess ? (
				<div className='flex flex-wrap gap-2'>
					<Button type='button' variant='outline' disabled={controlsDisabled} onClick={openFilePicker}>
						{isPreviewing ? 'Анализ…' : file ? 'Другой файл' : 'Обзор…'}
					</Button>
					<Button type='button' disabled={!canImport} onClick={() => void handleImport()}>
						{isImporting ? 'Импорт…' : 'Импортировать'}
					</Button>
				</div>
			) : null}

			{hint ? <p className='text-xs text-muted-foreground'>{hint}</p> : null}
			{previewError ? <p className='text-xs text-destructive'>{previewError}</p> : null}
			{importError ? <p className='text-xs text-destructive'>{importError}</p> : null}

			{preview && !isSuccess ? (
				<div className='grid gap-1 text-xs sm:grid-cols-2 rounded-md border border-border bg-card/50 p-3'>
					<p>Валидных: {preview.validCount}</p>
					<p>Невалидных: {preview.invalidLines}</p>
					<p>Дубликаты в файле: {preview.duplicatesRemoved}</p>
					<p>Формат: {proxyFormatLabel(preview.detectedFormat)}</p>
					<p>Будет добавлено: {preview.wouldAdd}</p>
					<p>Будет обновлено: {preview.wouldUpdate}</p>
					{preview.sample.length > 0 ? (
						<div className='sm:col-span-2 flex flex-col gap-1'>
							<p className='text-muted-foreground'>Примеры:</p>
							{preview.sample.map(line => (
								<code key={line} className='break-all text-[11px]'>
									{line}
								</code>
							))}
						</div>
					) : null}
				</div>
			) : null}
		</div>
	);
}
